import { z } from 'zod';

/**
 * The wire shapes the media endpoints speak, mirroring `apps/api/src/media/media-schemas.ts`.
 *
 * They are duplicated rather than shared because task 11 owns the API's media folder and this
 * one, but not `@tailoring-crm/shared`, which several tasks are editing in parallel — an add/add
 * there would be a merge conflict for someone. The duplication is safe because these schemas are
 * parsed on arrival by the API client, so a divergence from the API's copy surfaces as a loud
 * contract error rather than an empty screen. Promoting the pair into the shared package is a
 * tidy-up available to whichever task next has reason to touch it, not a redesign.
 */

export const MEDIA_VARIANTS = ['full', 'thumb'] as const;
export type MediaVariant = (typeof MEDIA_VARIANTS)[number];

export const mediaSchema = z.object({
  id: z.string(),
  entityType: z.string(),
  entityId: z.string().nullable(),
  kind: z.string(),
  visibility: z.string(),
  status: z.string(),
  contentType: z.string(),
  originalFilename: z.string(),
  caption: z.string().nullable(),
  width: z.number(),
  height: z.number(),
  bytes: z.number(),
  originalBytes: z.number().nullable(),
  sortOrder: z.number(),
  /**
   * Which gallery folder the image is filed in, mirroring the API's `folderId`.
   *
   * OPTIONAL, not merely nullable, and it must stay that way: this schema parses live API
   * responses, and a server that predates task 16 omits the key entirely. Making it required
   * would fail the parse on every image and empty every gallery in the product.
   */
  folderId: z.string().nullable().optional(),
  createdAt: z.string(),
  thumbnailUrl: z.string().nullable(),
  thumbnailUrlExpiresAt: z.string().nullable(),
});
export type Media = z.infer<typeof mediaSchema>;

export const mediaListSchema = z.object({ items: z.array(mediaSchema) });

const presignedTargetSchema = z.object({
  url: z.string(),
  method: z.literal('PUT'),
  headers: z.record(z.string()),
});

export const uploadTicketSchema = z.object({
  clientId: z.string(),
  mediaId: z.string(),
  expiresAt: z.string(),
  upload: z.object({ full: presignedTargetSchema, thumb: presignedTargetSchema }),
});
export type UploadTicket = z.infer<typeof uploadTicketSchema>;

export const uploadTicketsSchema = z.object({ tickets: z.array(uploadTicketSchema) });

export const readUrlSchema = z.object({
  url: z.string(),
  expiresAt: z.string(),
  variant: z.enum(MEDIA_VARIANTS),
});
export type ReadUrl = z.infer<typeof readUrlSchema>;

/** The public, grant-authorised media shapes (task 11). */
export const publicMediaSchema = z.object({
  id: z.string(),
  caption: z.string().nullable(),
  width: z.number(),
  height: z.number(),
  thumbnailUrl: z.string().nullable(),
  thumbnailUrlExpiresAt: z.string().nullable(),
});
export const publicMediaListSchema = z.object({ items: z.array(publicMediaSchema) });
export type PublicMedia = z.infer<typeof publicMediaSchema>;
